import React, { useContext } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import config from 'constants/config'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import CloseButton from 'components/Common/CloseButton'
import AgentAvatar from 'supportAssistant/components/Support/AgentAvatar'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import { MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF } from 'constants/charterFlight'

// Animation for sliding in from bottom
const slideInFromBottom = keyframes`
  from {
    transform: translateY(120%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

const slideOutToBottom = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(120%);
    opacity: 0;
  }
`

// Desktop: Fixed container at bottom-right of viewport
const DesktopContainer = styled.div<{ show: boolean }>`
  position: fixed;
  bottom: ${rem(28)};
  right: ${rem(28)};
  z-index: ${zIndex.stickyPromptCard};
  animation: ${props => props.show ? slideInFromBottom : slideOutToBottom} 0.3s ease-out;
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  
  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const DesktopPane = styled(Pane)`
  width: ${rem(300)};
`

// Mobile: Bottom banner
const MobileBanner = styled(Pane)<{ show: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex.stickyFooter};
  box-shadow: ${props => props.theme.shadow.top.small};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s ease-out;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const HeaderGroup = styled(Group)`
  position: relative;
  display: flex;
  justify-content: center;
`

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`

interface Props {
  show?: boolean
  onClose?: () => void
}

function MaldivesCallPrompt(props: Props) {
  const {
    show = false,
    onClose,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const handleScheduleCallback = () => {
    showModal(<RequestAgentCallbackModal type="SALES" />)
  }

  const haveQuestionsText = intl.formatMessage({
    id: 'Zc72D2cxy7',
    defaultMessage: 'Have questions?',
    description: 'Heading for Maldives charter call prompt card',
  })

  const talkToExpertText = intl.formatMessage({
    id: '4uQG33m0z1',
    defaultMessage: 'Talk to a Maldives travel expert today.',
    description: 'Body text encouraging contact with Maldives travel expert',
  })

  const scheduleCallbackMobileText = intl.formatMessage({
    id: 'uXg09DWOzc',
    defaultMessage: 'Schedule a call back',
    description: 'Mobile link text to schedule a callback from Maldives travel expert',
  })

  const callNowText = intl.formatMessage({
    id: '4SIkF4xy//',
    defaultMessage: 'Call now',
    description: 'Mobile button text to call Maldives travel expert',
  })

  if (!show) {
    return null
  }

  return (
    <>
      {/* Desktop: Floating card at bottom-right */}
      <DesktopContainer show={show}>
        <DesktopPane padding="20 20 28 20" border="neutral-five" borderRadius="S" shadow="bottom-medium">
          <VerticalSpacer gap={12}>
            <HeaderGroup direction="horizontal" horizontalAlign="center">
              <AgentAvatar width={68} height={68} />
              <CloseButtonWrapper>
                <CloseButton onClick={onClose} />
              </CloseButtonWrapper>
            </HeaderGroup>
            <Group direction="vertical" gap={0} horizontalAlign="center">
              <Heading variant="heading4" align="center" colour="highlight-secondary">
                {haveQuestionsText}
              </Heading>
              <BodyText variant="medium" align="center">
                {talkToExpertText}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={8} horizontalAlign="center">
              {/* Phone Number */}
              <TextLink
                href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
                startIcon={<LinePhoneIcon />}
                size="large"
                underline={false}
                block
                rel="nofollow"
              >
                <Heading variant="heading5" as="span">
                  {config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER}
                </Heading>
              </TextLink>

              {/* Schedule callback link */}
              {/* TODO: i18n flagged — trailing space before link removed by using FormattedMessage */}
              <BodyText variant="medium" align="center">
                <FormattedMessage
                  id="EYFw62OMaZ"
                  defaultMessage="or <link>schedule a call back</link>"
                  description="Link to schedule a callback with a Maldives travel expert"
                  values={{
                    link: (chunks) => (
                      <TextLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          handleScheduleCallback()
                        }}
                        underline
                      >
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </BodyText>
            </Group>
          </VerticalSpacer>
        </DesktopPane>
      </DesktopContainer>

      {/* Mobile: Bottom banner */}
      <MobileBanner show={show} padding={20}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
          <Group direction="vertical" gap={0}>
            <BodyText variant="medium" weight="bold">
              {haveQuestionsText}
            </BodyText>
            <TextLink
              href="#"
              onClick={(e) => {
                e.preventDefault()
                handleScheduleCallback()
              }}
              size="small"
              underline
            >
              {scheduleCallbackMobileText}
            </TextLink>
          </Group>
          <TextButton
            kind="secondary"
            size="medium"
            startIcon={<LinePhoneIcon />}
            href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
            rel="nofollow"
          >
            {callNowText}
          </TextButton>
        </Group>
      </MobileBanner>
    </>
  )
}

export default MaldivesCallPrompt
